import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Sparkles, ExternalLink, Trash2, Save, Calendar, Bell, StickyNote,
  ChevronRight, Bookmark
} from "lucide-react";

const STAGES = ["Matched", "Interested", "Applied", "Interview", "Follow-up", "Offer", "Rejected"];

const INVALID_HOSTS = ["example.com", "example.org", "example.net", "localhost", "127.0.0.1", "test.com", "dummy.com", "placeholder.com", "foo.com", "bar.com"];
function isValidApplyUrl(url) {
  if (!url || typeof url !== "string") return false;
  const u = url.trim().toLowerCase();
  if (!(u.startsWith("http://") || u.startsWith("https://"))) return false;
  for (const bad of INVALID_HOSTS) {
    if (u.includes(`//${bad}`) || u.includes(`//www.${bad}`)) return false;
  }
  if (u.includes("placeholder") || u.includes("dummy")) return false;
  return true;
}

const STAGE_META = {
  Matched: { bg: "rgba(157,0,255,0.14)", dot: "#9D00FF" },
  Interested: { bg: "rgba(0,229,255,0.12)", dot: "#00E5FF" },
  Applied: { bg: "rgba(255,209,102,0.14)", dot: "#FFD166" },
  Interview: { bg: "rgba(157,0,255,0.14)", dot: "#C580FF" },
  "Follow-up": { bg: "rgba(255,0,127,0.12)", dot: "#FF6DAE" },
  Offer: { bg: "rgba(128,255,234,0.14)", dot: "#80FFEA" },
  Rejected: { bg: "rgba(255,255,255,0.06)", dot: "#64748B" },
};

export default function Pipeline() {
  const [grouped, setGrouped] = useState({});
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // job_id currently expanded

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/job-agent/saved");
      setGrouped(r.data?.grouped || {});
      setCount(r.data?.count || 0);
    } catch {
      toast.error("Could not load pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStage = async (item, newStage) => {
    try {
      await api.post("/job-agent/save", {
        job_id: item.job_id,
        stage: newStage,
        notes: item.notes || "",
        applied_date: item.applied_date || "",
        follow_up_date: item.follow_up_date || "",
      });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const saveMeta = async (item, patch) => {
    try {
      await api.post("/job-agent/save", {
        job_id: item.job_id,
        stage: item.stage,
        notes: patch.notes ?? item.notes ?? "",
        applied_date: patch.applied_date ?? item.applied_date ?? "",
        follow_up_date: patch.follow_up_date ?? item.follow_up_date ?? "",
      });
      toast.success("Saved");
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove ${item.job_snapshot?.title || "this job"} from your pipeline?`)) return;
    try {
      await api.delete(`/job-agent/saved/${item.job_id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="space-y-8" data-testid="pipeline-root">
      <div className="fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="chip mb-3"><Sparkles size={12} /> Pipeline</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <span className="text-neon">Your job pipeline.</span>
          </h1>
          <p className="mt-3 text-slate-400 text-lg max-w-2xl leading-relaxed">
            Move roles across stages, add notes, and never lose track of a follow-up.
          </p>
        </div>
        <Link
          to="/job-agent"
          data-testid="go-job-agent"
          className="btn-neon inline-flex items-center gap-2"
        >
          Find more matches <ChevronRight size={16} />
        </Link>
      </div>

      {loading ? (
        <div className="text-slate-400" data-testid="pipeline-loading">Loading pipeline…</div>
      ) : count === 0 ? (
        <div className="glass p-10 text-center" data-testid="pipeline-empty">
          <Bookmark size={32} className="mx-auto text-slate-600" />
          <div className="font-display font-bold text-xl mt-3 text-white">No saved jobs yet.</div>
          <div className="text-slate-400 mt-2 mb-5">Save a match from the Job Agent to start tracking.</div>
          <Link to="/job-agent" className="btn-neon inline-flex items-center gap-2">
            Browse matches <ChevronRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-flow-col auto-cols-[minmax(280px,_1fr)] gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
          {STAGES.map((stage) => {
            const items = grouped[stage] || [];
            const meta = STAGE_META[stage];
            return (
              <div key={stage} className="glass flex flex-col min-h-[300px]"
                data-testid={`stage-col-${stage}`}
                data-stage={stage}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shadow-[0_0_8px_currentColor]" style={{ background: meta.dot, color: meta.dot }} />
                    <div className="font-display font-bold text-sm text-white">{stage}</div>
                  </div>
                  <div className="text-xs font-bold text-white rounded-full px-2.5 py-0.5 border border-white/15" style={{ background: meta.bg, boxShadow: `0 0 12px ${meta.dot}55` }} data-testid={`stage-count-${stage}`} data-stage={stage}>{items.length}</div>
                </div>
                <div className="p-3 space-y-3 flex-1">
                  {items.length === 0 && (
                    <div className="text-xs text-slate-500 text-center py-6">Drop roles here</div>
                  )}
                  {items.map((item) => (
                    <PipelineCard
                      key={item.job_id}
                      item={item}
                      expanded={editing === item.job_id}
                      onExpand={() => setEditing(editing === item.job_id ? null : item.job_id)}
                      onStageChange={(s) => updateStage(item, s)}
                      onSaveMeta={(patch) => saveMeta(item, patch)}
                      onRemove={() => remove(item)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PipelineCard({ item, expanded, onExpand, onStageChange, onSaveMeta, onRemove }) {
  const [notes, setNotes] = useState(item.notes || "");
  const [appliedDate, setAppliedDate] = useState(item.applied_date || "");
  const [followUp, setFollowUp] = useState(item.follow_up_date || "");
  const snap = item.job_snapshot || {};

  useEffect(() => {
    setNotes(item.notes || "");
    setAppliedDate(item.applied_date || "");
    setFollowUp(item.follow_up_date || "");
  }, [item.job_id, item.notes, item.applied_date, item.follow_up_date]);

  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.03] hover:border-[#00E5FF]/30 transition-colors" data-testid={`pipeline-item-${item.job_id}`}>
      <button
        onClick={onExpand}
        data-testid={`pipeline-expand-${item.job_id}`}
        className="w-full text-left p-3"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-bold text-sm leading-tight truncate text-white">{snap.title || "Untitled"}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{snap.company} · {snap.location}</div>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/5 p-3 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><Bookmark size={10} /> Stage</label>
            <select
              data-testid={`pipeline-stage-select-${item.job_id}`}
              value={item.stage}
              onChange={(e) => onStageChange(e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 text-white px-2 py-1.5 text-xs"
            >
              {STAGES.map((s) => <option key={s} value={s} className="bg-[#0A0A12]">{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><Calendar size={10} /> Applied</label>
              <input
                type="date"
                data-testid={`pipeline-applied-${item.job_id}`}
                value={appliedDate}
                onChange={(e) => setAppliedDate(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 text-white px-2 py-1.5 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><Bell size={10} /> Follow-up</label>
              <input
                type="date"
                data-testid={`pipeline-followup-${item.job_id}`}
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 text-white px-2 py-1.5 text-xs"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><StickyNote size={10} /> Notes</label>
            <textarea
              data-testid={`pipeline-notes-${item.job_id}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Recruiter, contact person, key highlights…"
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 text-white px-2 py-1.5 text-xs resize-none placeholder:text-slate-500"
            />
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-2">
              {isValidApplyUrl(snap.apply_url) && (
                <a
                  href={snap.apply_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`pipeline-apply-${item.job_id}`}
                  className="btn-glass inline-flex items-center gap-1 !px-2 !py-1.5 text-[11px]"
                >
                  <ExternalLink size={11} /> Apply
                </a>
              )}
              <button
                data-testid={`pipeline-remove-${item.job_id}`}
                onClick={onRemove}
                className="inline-flex items-center gap-1 rounded-xl border border-[#FF007F]/40 text-[#FF6DAE] bg-[#FF007F]/10 px-2 py-1.5 text-[11px] font-bold hover:bg-[#FF007F]/20"
              >
                <Trash2 size={11} /> Remove
              </button>
            </div>
            <button
              data-testid={`pipeline-save-meta-${item.job_id}`}
              onClick={() => onSaveMeta({ notes, applied_date: appliedDate, follow_up_date: followUp })}
              className="btn-neon inline-flex items-center gap-1 !px-3 !py-1.5 text-[11px]"
            >
              <Save size={11} /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
